import {
  getUiWalletAccountStorageKey,
  UiWallet,
  UiWalletAccount,
  uiWalletAccountBelongsToUiWallet,
  uiWalletAccountsAreSame,
  useWallets,
} from "@wallet-standard/react"
import { useEffect, useMemo, useState } from "react"

import {
  SelectedWalletAccountContext,
  SelectedWalletAccountState,
} from "./SelectedWalletAccountContext"

const STORAGE_KEY = "solana-wallet-standard-example-react:selected-wallet-and-address"

let wasSetterInvoked = false

// Safe version that handles potential errors when accessing wallet properties
function getSavedWalletAccount(wallets: readonly UiWallet[]): UiWalletAccount | undefined {
  if (wasSetterInvoked) {
    // After the user makes an explicit choice of wallet, stop trying to auto-select the
    // saved wallet, if and when it appears.
    return
  }

  try {
    const savedWalletNameAndAddress = localStorage.getItem(STORAGE_KEY)
    if (!savedWalletNameAndAddress || typeof savedWalletNameAndAddress !== "string") {
      return
    }

    const [savedWalletName, savedAccountAddress] = savedWalletNameAndAddress.split(":")
    if (!savedWalletName || !savedAccountAddress) {
      return
    }

    for (const wallet of wallets) {
      // Safety check to prevent accessing invalid wallet
      if (!wallet || typeof wallet !== "object" || !wallet.name) continue

      if (wallet.name === savedWalletName) {
        // Safety check for accounts array
        if (!Array.isArray(wallet.accounts)) continue

        for (const account of wallet.accounts) {
          if (account && account.address === savedAccountAddress) {
            return account
          }
        }
      }
    }
  } catch (error) {
    console.error("Error getting saved wallet account:", error)
  }

  return undefined
}

export function SelectedWalletAccountContextProvider({ children }: { children: React.ReactNode }) {
  const wallets = useWallets()
  const [selectedWalletAccount, setSelectedWalletAccountInternal] =
    useState<SelectedWalletAccountState>(() => {
      try {
        return getSavedWalletAccount(wallets)
      } catch (error) {
        console.error("Error initializing wallet account:", error)
        return undefined
      }
    })

  const setSelectedWalletAccount: React.Dispatch<
    React.SetStateAction<SelectedWalletAccountState>
  > = (setStateAction) => {
    setSelectedWalletAccountInternal((prevSelectedWalletAccount) => {
      try {
        wasSetterInvoked = true
        const nextWalletAccount =
          typeof setStateAction === "function"
            ? setStateAction(prevSelectedWalletAccount)
            : setStateAction
        const accountKey = nextWalletAccount
          ? getUiWalletAccountStorageKey(nextWalletAccount)
          : undefined
        if (accountKey) {
          localStorage.setItem(STORAGE_KEY, accountKey)
        } else {
          localStorage.removeItem(STORAGE_KEY)
        }
        return nextWalletAccount
      } catch (error) {
        console.error("Error setting wallet account:", error)
        return undefined
      }
    })
  }

  // Safe check for wallet availability
  useEffect(() => {
    try {
      const savedWalletAccount = getSavedWalletAccount(wallets)
      if (savedWalletAccount) {
        setSelectedWalletAccountInternal(savedWalletAccount)
      }
    } catch (error) {
      console.error("Error checking for saved wallet:", error)
    }
  }, [wallets])

  // Safely find matching wallet account
  const walletAccount = useMemo(() => {
    if (!selectedWalletAccount) return undefined

    try {
      for (const uiWallet of wallets) {
        // Skip invalid wallets
        if (!uiWallet || !Array.isArray(uiWallet.accounts)) continue

        // Check each account for a match
        for (const uiWalletAccount of uiWallet.accounts) {
          try {
            if (uiWalletAccountsAreSame(selectedWalletAccount, uiWalletAccount)) {
              return uiWalletAccount
            }
          } catch (e) {
            console.error("Error comparing wallet accounts:", e)
            // Silently skip this account if comparison fails
            continue
          }
        }

        // If no exact match, try to find an account from the same wallet
        try {
          if (
            uiWalletAccountBelongsToUiWallet(selectedWalletAccount, uiWallet) &&
            uiWallet.accounts[0]
          ) {
            return uiWallet.accounts[0]
          }
        } catch (e) {
          // Silently skip if this check fails
          console.error("Error checking wallet account:", e)
          continue
        }
      }
    } catch (error) {
      console.error("Error finding matching wallet account:", error)
    }

    return undefined
  }, [selectedWalletAccount, wallets])

  // Clear selection if wallet disconnects
  useEffect(() => {
    if (selectedWalletAccount && !walletAccount) {
      setSelectedWalletAccountInternal(undefined)
    }
  }, [selectedWalletAccount, walletAccount])

  // Provide safe values to context
  return (
    <SelectedWalletAccountContext.Provider
      value={useMemo(() => [walletAccount, setSelectedWalletAccount], [walletAccount])}
    >
      {children}
    </SelectedWalletAccountContext.Provider>
  )
}
